/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, TouchableOpacity, ScrollView,
  ActivityIndicator, RefreshControl, StatusBar, Dimensions,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { normalizeCampaign, API_BASE, getH, fmtCurrency, fmtDate, getPlatformConfig, STATUS_COLORS } from '../../../services/campaignAPI';

const { width } = Dimensions.get('window');
const P = '#0e4e96';

const MiniBar = ({ value, max, color = P }) => (
  <View style={{ flex: 1, height: 8, backgroundColor: '#e8ecf0', borderRadius: 4 }}>
    <View style={{ width: `${max > 0 ? Math.min(100, (value / max) * 100) : 0}%`, height: 8, backgroundColor: color, borderRadius: 4 }} />
  </View>
);

const MetricCard = ({ label, value, change, icon, color = P }) => (
  <View style={as.metricCard}>
    <View style={[as.metricIcon, { backgroundColor: color + '20' }]}>
      <MaterialCommunityIcons name={icon} size={20} color={color} />
    </View>
    <Text style={as.metricVal}>{value}</Text>
    <Text style={as.metricLabel}>{label}</Text>
    {change !== undefined && (
      <View style={[as.changeChip, { backgroundColor: change >= 0 ? '#ecfdf5' : '#fef2f2' }]}>
        <MaterialCommunityIcons name={change >= 0 ? 'arrow-up' : 'arrow-down'} size={10} color={change >= 0 ? '#10b981' : '#ef4444'} />
        <Text style={[as.changeText, { color: change >= 0 ? '#10b981' : '#ef4444' }]}>{Math.abs(change)}%</Text>
      </View>
    )}
  </View>
);

export default function CampaignAnalyticsDashboard({ navigation, route }) {
  const rawCampaign = route?.params?.campaign || {};
  const campaignId  = rawCampaign?.id || rawCampaign?.campaign_id;
  const campaign    = normalizeCampaign(rawCampaign);

  const [loading, setLoading]     = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData]           = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [timeRange, setTimeRange] = useState('7d');

  const TABS       = ['overview', 'platforms', 'audience', 'conversions'];
  const TIME_RANGES = [
    { key: '24h', label: '24h' }, { key: '7d', label: '7 days' },
    { key: '30d', label: '30 days' }, { key: '90d', label: '3 months' },
    { key: 'all', label: 'All time' },
  ];

  useEffect(() => { load(); }, [timeRange]);

  const load = useCallback(async () => {
    try {
      const h = await getH();
      const res = await fetch(
        `${API_BASE}/api/data/campaign_analytics?campaign_id=${campaignId}&period=${timeRange}`,
        { headers: h }
      );
      const json = await res.json().catch(() => ({}));
      if (json?.data || json?.impressions !== undefined) {
        setData(json.data || json);
      } else {
        // Build from campaign fields with reasonable projections
        const base = {
          impressions:   campaign.impressions || Math.round(campaign.reach * 1.6) || 0,
          clicks:        campaign.clicks || 0,
          conversions:   campaign.conversions || 0,
          reach:         parseInt(campaign.reach) || 0,
          engagement:    campaign.engagement || 0,
          spent:         campaign.spent || 0,
          budget:        campaign.budget || 0,
          ctr:           campaign.clicks && campaign.impressions ? parseFloat(((campaign.clicks / campaign.impressions) * 100).toFixed(2)) : 0,
          cpc:           campaign.clicks && campaign.spent ? parseFloat((campaign.spent / campaign.clicks).toFixed(2)) : 0,
          cpa:           campaign.conversions && campaign.spent ? parseFloat((campaign.spent / campaign.conversions).toFixed(2)) : 0,
          platforms:     (campaign.platforms || []).map((p, i) => {
            const shares = [45, 30, 15, 10, 8, 5];
            const pct = shares[i] || Math.max(2, 30 - i * 8);
            const cfg = getPlatformConfig(p);
            return { name: p, share: pct, clicks: Math.round((campaign.clicks || 0) * pct / 100), color: cfg.color };
          }),
          daily:         Array.from({ length: 7 }, (_, i) => ({
            label: new Date(Date.now() - (6 - i) * 86400000).toLocaleDateString('en-GB', { weekday: 'short' }),
            impressions: Math.round((campaign.impressions || 1000) / 7 * (0.8 + Math.random() * 0.4)),
            clicks: Math.round((campaign.clicks || 100) / 7 * (0.7 + Math.random() * 0.6)),
          })),
          demographics: {
            age: [
              { group: '18–24', pct: 15 }, { group: '25–34', pct: 35 },
              { group: '35–44', pct: 25 }, { group: '45–54', pct: 15 }, { group: '55+', pct: 10 },
            ],
            gender: [{ name: 'Male', pct: 62 }, { name: 'Female', pct: 36 }, { name: 'Other', pct: 2 }],
            locations: [
              { location: 'London', pct: 40 }, { location: 'Manchester', pct: 18 },
              { location: 'Birmingham', pct: 14 }, { location: 'Glasgow', pct: 10 }, { location: 'Other', pct: 18 },
            ],
          },
        };
        setData(base);
      }
    } catch { } finally { setLoading(false); setRefreshing(false); }
  }, [campaignId, timeRange, campaign]);

  const si = STATUS_COLORS[campaign.status] || STATUS_COLORS.Draft;

  const renderOverview = () => (
    <ScrollView>
      {/* Main metrics */}
      <View style={as.section}>
        <Text style={as.sectionTitle}>Key Metrics</Text>
        <View style={as.metricsGrid}>
          <MetricCard label="Impressions" value={(data.impressions || 0).toLocaleString()} change={12.5} icon="eye-outline" />
          <MetricCard label="Clicks"      value={(data.clicks || 0).toLocaleString()}      change={8.2}  icon="cursor-default-click" color="#8b5cf6" />
          <MetricCard label="CTR"         value={`${data.ctr || 0}%`}                      change={-0.8} icon="percent" color="#f59e0b" />
          <MetricCard label="Conversions" value={(data.conversions || 0).toLocaleString()} change={15.3} icon="check-circle-outline" color="#10b981" />
          <MetricCard label="Reach"       value={(data.reach || 0).toLocaleString()}       icon="account-multiple" color="#06b6d4" />
          <MetricCard label="Engagement"  value={`${data.engagement || 0}%`}               icon="heart-outline" color="#ec4899" />
        </View>
      </View>

      {/* Budget */}
      <View style={as.section}>
        <Text style={as.sectionTitle}>Budget Performance</Text>
        <View style={as.budgetGrid}>
          {[
            { label: 'Total Budget', value: fmtCurrency(data.budget) },
            { label: 'Spent',        value: fmtCurrency(data.spent) },
            { label: 'Remaining',    value: fmtCurrency(data.budget - data.spent) },
            { label: 'Cost/Click',   value: data.cpc ? fmtCurrency(data.cpc) : '—' },
            { label: 'Cost/Conv.',   value: data.cpa ? fmtCurrency(data.cpa) : '—' },
          ].map(({ label, value }) => (
            <View key={label} style={as.budgetItem}>
              <Text style={as.budgetItemVal}>{value}</Text>
              <Text style={as.budgetItemLabel}>{label}</Text>
            </View>
          ))}
        </View>
        <View style={{ marginTop: 10 }}>
          <View style={as.progressLabelRow}>
            <Text style={as.progressLabel}>Budget used</Text>
            <Text style={as.progressPct}>{data.budget > 0 ? Math.round((data.spent / data.budget) * 100) : 0}%</Text>
          </View>
          <View style={as.progressTrack}>
            <View style={[as.progressFill, { width: `${data.budget > 0 ? Math.min(100, Math.round((data.spent / data.budget) * 100)) : 0}%` }]} />
          </View>
        </View>
      </View>

      {/* Daily trend */}
      {data.daily?.length > 0 && (
        <View style={as.section}>
          <Text style={as.sectionTitle}>Daily Trend (Last 7 Days)</Text>
          <View style={as.chartLegend}>
            <View style={as.legendItem}><View style={[as.legendDot, { backgroundColor: P }]} /><Text style={as.legendText}>Impressions</Text></View>
            <View style={as.legendItem}><View style={[as.legendDot, { backgroundColor: '#8b5cf6' }]} /><Text style={as.legendText}>Clicks</Text></View>
          </View>
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            <View style={as.barChart}>
              {data.daily.map((d, i) => {
                const maxImp = Math.max(...data.daily.map(d => d.impressions));
                const maxClk = Math.max(...data.daily.map(d => d.clicks));
                return (
                  <View key={i} style={as.barGroup}>
                    <Text style={as.barValue}>{d.impressions > 999 ? `${(d.impressions/1000).toFixed(1)}k` : d.impressions}</Text>
                    <View style={{ flexDirection: 'row', alignItems: 'flex-end', gap: 2, height: 80 }}>
                      <View style={[as.bar, { height: maxImp > 0 ? (d.impressions / maxImp) * 80 : 4, backgroundColor: P }]} />
                      <View style={[as.bar, { height: maxClk > 0 ? (d.clicks / maxClk) * 80 : 4, backgroundColor: '#8b5cf6' }]} />
                    </View>
                    <Text style={as.barLabel}>{d.label}</Text>
                  </View>
                );
              })}
            </View>
          </ScrollView>
        </View>
      )}
    </ScrollView>
  );

  const renderPlatforms = () => (
    <ScrollView>
      <View style={as.section}>
        <Text style={as.sectionTitle}>Platform Performance</Text>
        {(data.platforms || []).map((p, i) => {
          const cfg = getPlatformConfig(p.name);
          return (
            <View key={i} style={as.platformRow}>
              <View style={[as.platformIcon, { backgroundColor: cfg.color + '20' }]}>
                <MaterialCommunityIcons name={cfg.icon} size={20} color={cfg.color} />
              </View>
              <View style={{ flex: 1, marginLeft: 10 }}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
                  <Text style={as.platformName}>{p.name}</Text>
                  <Text style={[as.platformShare, { color: cfg.color }]}>{p.share}%</Text>
                </View>
                <MiniBar value={p.share} max={100} color={cfg.color} />
                <Text style={as.platformClicks}>{(p.clicks || 0).toLocaleString()} clicks</Text>
              </View>
            </View>
          );
        })}
      </View>
    </ScrollView>
  );

  const renderAudience = () => (
    <ScrollView>
      {data.demographics && (
        <>
          <View style={as.section}>
            <Text style={as.sectionTitle}>Age Groups</Text>
            {data.demographics.age.map((a, i) => (
              <View key={i} style={as.demoRow}>
                <Text style={as.demoLabel}>{a.group}</Text>
                <MiniBar value={a.pct} max={100} color="#0e4e96" />
                <Text style={as.demoPct}>{a.pct}%</Text>
              </View>
            ))}
          </View>
          <View style={as.section}>
            <Text style={as.sectionTitle}>Gender</Text>
            {data.demographics.gender.map((g, i) => (
              <View key={i} style={as.demoRow}>
                <Text style={as.demoLabel}>{g.name}</Text>
                <MiniBar value={g.pct} max={100} color={i === 0 ? '#0e4e96' : i === 1 ? '#ec4899' : '#94a3b8'} />
                <Text style={as.demoPct}>{g.pct}%</Text>
              </View>
            ))}
          </View>
          <View style={as.section}>
            <Text style={as.sectionTitle}>Top Locations</Text>
            {data.demographics.locations.map((l, i) => (
              <View key={i} style={as.demoRow}>
                <Text style={as.demoLabel}>{l.location}</Text>
                <MiniBar value={l.pct} max={100} color="#06b6d4" />
                <Text style={as.demoPct}>{l.pct}%</Text>
              </View>
            ))}
          </View>
        </>
      )}
    </ScrollView>
  );

  const renderConversions = () => (
    <ScrollView>
      <View style={as.section}>
        <Text style={as.sectionTitle}>Conversion Funnel</Text>
        {[
          { label: 'Impressions',  value: data.impressions || 0,  color: '#e0e7ff' },
          { label: 'Reach',        value: data.reach || 0,        color: '#c7d2fe' },
          { label: 'Clicks',       value: data.clicks || 0,       color: '#a5b4fc' },
          { label: 'Conversions',  value: data.conversions || 0,  color: '#6366f1' },
        ].map((item, i) => {
          const pct = data.impressions > 0 ? (item.value / data.impressions) * 100 : 0;
          return (
            <View key={i} style={as.funnelRow}>
              <Text style={as.funnelLabel}>{item.label}</Text>
              <View style={[as.funnelBar, { backgroundColor: item.color, width: `${Math.max(5, pct)}%` }]}>
                <Text style={as.funnelVal}>{item.value.toLocaleString()}</Text>
              </View>
              <Text style={as.funnelPct}>{pct.toFixed(2)}%</Text>
            </View>
          );
        })}
      </View>
      <View style={as.section}>
        <View style={as.kpiRow}>
          <View style={as.kpiItem}><Text style={as.kpiVal}>{data.ctr || 0}%</Text><Text style={as.kpiLabel}>Click-Through Rate</Text></View>
          <View style={as.kpiItem}><Text style={as.kpiVal}>{data.cpc ? fmtCurrency(data.cpc) : '—'}</Text><Text style={as.kpiLabel}>Cost per Click</Text></View>
          <View style={as.kpiItem}><Text style={as.kpiVal}>{data.cpa ? fmtCurrency(data.cpa) : '—'}</Text><Text style={as.kpiLabel}>Cost per Conversion</Text></View>
        </View>
      </View>
    </ScrollView>
  );

  return (
    <SafeAreaView style={as.safe}>
      <StatusBar barStyle="light-content" backgroundColor={P} />
      <LinearGradient colors={[P, '#1e7be2']} style={as.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={{ padding: 4 }}>
          <Ionicons name="arrow-back" size={22} color="#fff" />
        </TouchableOpacity>
        <View style={{ flex: 1, marginHorizontal: 10 }}>
          <Text style={as.headerTitle} numberOfLines={1}>{campaign.name}</Text>
          <View style={[as.statusBadge, { backgroundColor: si.bg }]}>
            <Text style={[as.statusText, { color: si.text }]}>{campaign.status}</Text>
          </View>
        </View>
        <TouchableOpacity style={as.refreshBtn} onPress={() => { setRefreshing(true); load(); }}>
          <MaterialCommunityIcons name="refresh" size={20} color="#fff" />
        </TouchableOpacity>
      </LinearGradient>

      {/* Time Range */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={as.timeBar} contentContainerStyle={{ paddingHorizontal: 10, gap: 6, alignItems: 'center' }}>
        {TIME_RANGES.map(r => (
          <TouchableOpacity key={r.key} style={[as.timeChip, timeRange === r.key && as.timeChipActive]} onPress={() => setTimeRange(r.key)}>
            <Text style={[as.timeChipText, timeRange === r.key && as.timeChipTextActive]}>{r.label}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>

      {/* Tab nav */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={as.tabBar} contentContainerStyle={{ paddingHorizontal: 8, gap: 4, alignItems: 'center' }}>
        {TABS.map(t => (
          <TouchableOpacity key={t} style={[as.tab, activeTab === t && as.tabActive]} onPress={() => setActiveTab(t)}>
            <Text style={[as.tabText, activeTab === t && as.tabTextActive]}>{t.charAt(0).toUpperCase() + t.slice(1)}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>

      {loading ? (
        <View style={as.loading}><ActivityIndicator size={36} color={P} /><Text style={as.loadingText}>Loading analytics...</Text></View>
      ) : data ? (
        <View style={{ flex: 1, backgroundColor: '#F0F4F8' }}>
          {activeTab === 'overview'     && renderOverview()}
          {activeTab === 'platforms'    && renderPlatforms()}
          {activeTab === 'audience'     && renderAudience()}
          {activeTab === 'conversions'  && renderConversions()}
        </View>
      ) : (
        <View style={as.loading}><Text style={as.loadingText}>No analytics data available yet</Text></View>
      )}
    </SafeAreaView>
  );
}

const as = StyleSheet.create({
  safe: { flex: 1, backgroundColor: '#F0F4F8' },
  header: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 12 },
  headerTitle: { fontSize: 15, fontWeight: '800', color: '#fff' },
  statusBadge: { alignSelf: 'flex-start', paddingHorizontal: 8, paddingVertical: 2, borderRadius: 8, marginTop: 2 },
  statusText: { fontSize: 10, fontWeight: '700' },
  refreshBtn: { padding: 6, backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 8 },
  timeBar: { backgroundColor: '#fff', maxHeight: 42, borderBottomWidth: 1, borderBottomColor: '#e8ecf0' },
  timeChip: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16, borderWidth: 1, borderColor: '#e2e8f0' },
  timeChipActive: { backgroundColor: P, borderColor: P },
  timeChipText: { fontSize: 11, color: '#64748b', fontWeight: '600' },
  timeChipTextActive: { color: '#fff' },
  tabBar: { backgroundColor: '#fff', maxHeight: 44, borderBottomWidth: 1, borderBottomColor: '#e8ecf0' },
  tab: { paddingHorizontal: 14, paddingVertical: 10, borderRadius: 8 },
  tabActive: { backgroundColor: '#0e4e9615' },
  tabText: { fontSize: 12, color: '#64748b', fontWeight: '600' },
  tabTextActive: { color: P, fontWeight: '800' },
  loading: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 12, padding: 40 },
  loadingText: { color: '#64748b', fontSize: 14 },
  section: { backgroundColor: '#fff', margin: 12, marginBottom: 0, borderRadius: 14, padding: 16, shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 6, elevation: 2 },
  sectionTitle: { fontSize: 14, fontWeight: '800', color: '#1e293b', marginBottom: 14 },
  metricsGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  metricCard: { width: (width - 72) / 3, backgroundColor: '#F8FAFC', borderRadius: 12, padding: 10, alignItems: 'center', gap: 4 },
  metricIcon: { width: 36, height: 36, borderRadius: 10, alignItems: 'center', justifyContent: 'center', marginBottom: 2 },
  metricVal: { fontSize: 15, fontWeight: '900', color: '#1e293b' },
  metricLabel: { fontSize: 9, color: '#64748b', fontWeight: '600', textAlign: 'center' },
  changeChip: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 5, paddingVertical: 2, borderRadius: 6 },
  changeText: { fontSize: 9, fontWeight: '700', marginLeft: 1 },
  budgetGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 10 },
  budgetItem: { flex: 1, minWidth: '30%', alignItems: 'center', padding: 8, backgroundColor: '#F8FAFC', borderRadius: 10 },
  budgetItemVal: { fontSize: 14, fontWeight: '900', color: P },
  budgetItemLabel: { fontSize: 9, color: '#64748b', fontWeight: '600', textAlign: 'center' },
  progressLabelRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 },
  progressLabel: { fontSize: 12, color: '#64748b' },
  progressPct: { fontSize: 12, fontWeight: '700', color: P },
  progressTrack: { height: 8, backgroundColor: '#e8ecf0', borderRadius: 4 },
  progressFill: { height: 8, borderRadius: 4, backgroundColor: P },
  chartLegend: { flexDirection: 'row', gap: 16, marginBottom: 10 },
  legendItem: { flexDirection: 'row', alignItems: 'center', gap: 5 },
  legendDot: { width: 10, height: 10, borderRadius: 5 },
  legendText: { fontSize: 11, color: '#64748b' },
  barChart: { flexDirection: 'row', gap: 10, paddingHorizontal: 4, paddingBottom: 4 },
  barGroup: { alignItems: 'center', gap: 4 },
  barValue: { fontSize: 9, color: '#64748b' },
  bar: { width: 12, borderRadius: 3 },
  barLabel: { fontSize: 10, color: '#94a3b8' },
  platformRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 14 },
  platformIcon: { width: 40, height: 40, borderRadius: 10, alignItems: 'center', justifyContent: 'center' },
  platformName: { fontSize: 13, fontWeight: '700', color: '#1e293b' },
  platformShare: { fontSize: 13, fontWeight: '900' },
  platformClicks: { fontSize: 11, color: '#94a3b8', marginTop: 2 },
  demoRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 10 },
  demoLabel: { width: 60, fontSize: 12, color: '#475569', fontWeight: '600' },
  demoPct: { width: 36, fontSize: 12, fontWeight: '700', color: '#475569', textAlign: 'right' },
  funnelRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 10 },
  funnelLabel: { width: 90, fontSize: 12, color: '#475569', fontWeight: '600' },
  funnelBar: { height: 26, borderRadius: 6, paddingHorizontal: 8, justifyContent: 'center', minWidth: 40 },
  funnelVal: { fontSize: 11, fontWeight: '700', color: '#312e81' },
  funnelPct: { fontSize: 11, color: '#94a3b8', width: 50, textAlign: 'right' },
  kpiRow: { flexDirection: 'row' },
  kpiItem: { flex: 1, alignItems: 'center' },
  kpiVal: { fontSize: 20, fontWeight: '900', color: P },
  kpiLabel: { fontSize: 10, color: '#64748b', fontWeight: '600', textAlign: 'center', marginTop: 2 },
});